import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import {
  format,
  money,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type CurrencyCode,
  type PaymentMethod,
} from '@iobiz/shared'

/**
 * Saisie d'un MONTANT plafonné — avoir, acompte.
 *
 * ⭐ Le plafond est la raison d'être de cette boîte. Rendre plus que ce qu'on a
 * encaissé, ou encaisser plus que ce qui reste dû, sont deux erreurs qu'aucun
 * écran ne rattrape ensuite : le serveur les refuse, mais un refus après coup
 * fait ressaisir. Le montant est donc borné À LA SAISIE, et le plafond est
 * affiché — un champ qui refuse sans dire jusqu'où doit dire jusqu'où.
 *
 * ⚠️ Les montants voyagent en PLUS PETITE UNITÉ (ADR-10). La saisie, elle, se
 * fait dans l'unité courante : 12 000 F, pas 1 200 000 centimes. La conversion
 * vit ici, une fois, plutôt que dans chaque appelant.
 */
export function MontantDialog({
  open,
  titre,
  description,
  avertissement,
  plafond,
  devise,
  libelleAction,
  avecMoyen = false,
  onClose,
  onValider,
}: {
  open: boolean
  titre: string
  description: string
  /** Ce que l'utilisateur doit savoir AVANT de valider, pas après. */
  avertissement?: string
  /**
   * Maximum autorisé, en plus petite unité.
   *
   * ⚠️ ABSENT = pas de plafond, et c'est un cas réel : on ne borne pas le fond
   * qu'on déclare au matin ni ce qu'on compte le soir. Le champ démarre alors
   * vide plutôt qu'au maximum, et n'affiche aucun « au plus ».
   */
  plafond?: number | undefined
  devise: CurrencyCode
  libelleAction: string
  avecMoyen?: boolean
  onClose: () => void
  onValider: (montant: number, moyen: PaymentMethod) => Promise<void> | void
}): JSX.Element {
  const [saisie, setSaisie] = useState('')
  const [moyen, setMoyen] = useState<PaymentMethod>('cash')
  const [enCours, setEnCours] = useState(false)

  // Le plafond comme valeur de départ : c'est le cas courant — on rembourse
  // tout, on solde tout — et le reste se corrige en tapant. Sans plafond, le
  // champ reste vide : proposer un montant qu'on n'a pas compté serait pire
  // que rien, puisqu'il suffirait de valider pour le voir enregistré.
  useEffect(() => {
    if (open) {
      setSaisie(plafond === undefined ? '' : String(plafond))
      setMoyen('cash')
    }
  }, [open, plafond])

  const montant = Number(saisie.replace(',', '.')) || 0
  const trop = plafond !== undefined && montant > plafond
  // ⚠️ Sans plafond, zéro est LÉGITIME : un tiroir peut être ouvert sans fond,
  // et il peut être compté vide. Seule une saisie négative est refusée.
  const nul = plafond === undefined ? montant < 0 : montant <= 0

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{titre}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 4 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {description}
        </Typography>

        <TextField
          autoFocus
          label="Montant"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value.replace(/[^\d.,]/g, ''))}
          error={trop}
          helperText={
            plafond === undefined
              ? ' '
              : trop
                ? `Maximum ${format(money(plafond, devise), 'fr-FR')}`
                : `Au plus ${format(money(plafond, devise), 'fr-FR')}`
          }
          inputProps={{ inputMode: 'decimal' }}
          fullWidth
        />

        {avecMoyen && (
          <TextField
            select
            label="Moyen de règlement"
            value={moyen}
            onChange={(e) => setMoyen(e.target.value as PaymentMethod)}
            fullWidth
          >
            {PAYMENT_METHODS.map((m) => (
              <MenuItem key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </MenuItem>
            ))}
          </TextField>
        )}

        {avertissement && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {avertissement}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Montant retenu
          </Typography>
          <Typography className="tabular" sx={{ fontWeight: 700 }}>
            {format(money(plafond === undefined ? montant : Math.min(montant, plafond), devise), 'fr-FR')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          disabled={trop || nul || enCours}
          onClick={async () => {
            setEnCours(true)
            try {
              await onValider(Math.round(montant), moyen)
            } finally {
              setEnCours(false)
            }
          }}
        >
          {enCours ? 'Enregistrement…' : libelleAction}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
